import { Event, PackingListItemMinimal, ItemType } from '../schema';
import type { PathSegment, RouteStructure } from './routeInference';
import type { ItemQuantityPlan } from './quantityCalculation';
import { computeTaskWarnings, getAvailableAtSource } from './inventoryCheckLogic';

/**
 * Requirement data for an item type across target events
 */
export interface Requirement {
    quantity: number;
    events: Event[];
}

/**
 * Operation data for task generation
 */
export interface PackingOperationInput {
    sourceEventIds: string[];
    targetEventIds: string[];
    sourceStorageLocationIds: string[];
    targetStorageLocationIds: string[];
}

/**
 * Packing task for moving items between locations
 */
export interface PackingTask {
    itemTypeId: string;
    quantity: number;
    fromEventId: string | null;
    fromStorageLocationId: string | null;
    toEventId: string | null;
    toStorageLocationId: string | null;
    ambiguous: boolean;
    warnings: Array<{
        type: 'insufficient' | 'unavailable';
        available?: number;
        required?: number;
        shortage?: number;
        message: string;
    }>;
}

/**
 * Collect requirements from packing lists for task generation
 *
 * Queries all target events' packing lists and aggregates total requirements by item type.
 *
 * @param targets - Array of target events
 * @param packingListItemsByEvent - Map of event ID to packing list items for that event
 * @returns Map of itemTypeId to requirement data (total quantity and list of events needing it)
 *
 * @example
 * ```typescript
 * const requirements = collectRequirements(
 *   [eventA, eventB],
 *   new Map([
 *     ['event-a-id', [item1, item2]],
 *     ['event-b-id', [item3]]
 *   ])
 * );
 * // Returns: {
 * //   'safety-vest-id': { quantity: 30, events: [eventA, eventB] },
 * //   'garbage-bag-id': { quantity: 100, events: [eventA] }
 * // }
 * ```
 */
export function collectRequirements(
    targets: Event[],
    packingListItemsByEvent: Map<string, PackingListItemMinimal[]>
): Map<string, Requirement> {
    const requirements = new Map<string, Requirement>();

    for (const target of targets) {
        const packingListItems = packingListItemsByEvent.get(target.id) || [];

        for (const item of packingListItems) {
            const itemTypeId = item.itemType?.id;
            if (!itemTypeId) continue;

            const quantity = item.quantity || 0;
            if (quantity <= 0) continue;

            const existing = requirements.get(itemTypeId);
            if (existing) {
                // Aggregate quantity for items needed by multiple targets
                existing.quantity += quantity;
                if (!existing.events.find(e => e.id === target.id)) {
                    existing.events.push(target);
                }
            } else {
                requirements.set(itemTypeId, {
                    quantity,
                    events: [target],
                });
            }
        }
    }

    return requirements;
}

/**
 * Identify all trailers at a source or target event
 *
 * Trailers are items where itemType.category.name = "Trailers"
 *
 * @param event - The event to check
 * @param packingListItemsByEvent - Map of event ID to packing list items for that event
 * @returns Array of itemTypes that are trailers at this event
 */
export function identifyTrailers(event: Event, packingListItemsByEvent: Map<string, PackingListItemMinimal[]>): ItemType[] {
    const packingListItems = packingListItemsByEvent.get(event.id) || [];
    const trailers: Array<{ id: string }> = [];

    for (const item of packingListItems) {
        const itemType = item.itemType;
        if (!itemType?.id) continue;

        const categoryName = itemType.category?.name || '';
        if (categoryName === 'Trailers') {
            trailers.push({ id: itemType.id });
        }
    }

    return trailers as ItemType[];
}

/**
 * Determine if a source event has an ambiguous trailer situation
 *
 * Ambiguous if:
 * - Source has multiple trailers (>1) AND
 * - Some (but not all) trailers are going to one or more targets
 *
 * Not ambiguous if:
 * - Source has single trailer
 * - All trailers from source going to same target
 * - No trailers from source going to targets
 *
 * @param source - Source event to check
 * @param targets - Array of target events
 * @param packingListItemsByEvent - Map of event ID to packing list items for that event
 * @returns true if ambiguous, false otherwise
 *
 * @example
 * Ambiguous scenario:
 * - Source Event A has Trailer 1 and Trailer 2
 * - Trailer 1 going to Target Event B
 * - Trailer 2 staying or going elsewhere
 * Result: AMBIGUOUS (can't tell which trailer has which items)
 */
export function determineAmbiguity(
    source: Event,
    targets: Event[],
    packingListItemsByEvent: Map<string, PackingListItemMinimal[]>
): boolean {
    // Get trailers at source event
    const sourceTrailers = identifyTrailers(source, packingListItemsByEvent);

    // Single trailer is never ambiguous
    if (sourceTrailers.length <= 1) {
        return false;
    }

    // Get trailers going to target events
    const targetTrailerIds = new Set<string>();
    for (const target of targets) {
        const targetTrailers = identifyTrailers(target, packingListItemsByEvent);
        for (const trailer of targetTrailers) {
            targetTrailerIds.add(trailer.id);
        }
    }

    // Check if any source trailers are going to targets
    const sourceTrailersGoingToTargets = sourceTrailers.filter(trailer => targetTrailerIds.has(trailer.id));

    // Ambiguous if:
    // - Source has multiple trailers AND
    // - Some (but not all) trailers are going to targets
    if (sourceTrailersGoingToTargets.length > 0 && sourceTrailersGoingToTargets.length < sourceTrailers.length) {
        return true;
    }

    return false;
}

/**
 * Same as determineAmbiguity but keyed by event IDs (for callers that only have IDs and packing lists).
 * Used by generatePackingTasksFromLpSolution so it does not need full Event[].
 */
export function determineAmbiguityByEventIds(
    sourceEventId: string,
    targetEventIds: string[],
    packingListItemsByEvent: Map<string, PackingListItemMinimal[]>
): boolean {
    const sourceTrailerIds = getTrailerIdsAtEvent(sourceEventId, packingListItemsByEvent);
    if (sourceTrailerIds.size <= 1) return false;

    const targetTrailerIds = new Set<string>();
    for (const tid of targetEventIds) {
        for (const id of Array.from(getTrailerIdsAtEvent(tid, packingListItemsByEvent))) targetTrailerIds.add(id);
    }
    const goingToTargets = Array.from(sourceTrailerIds).filter(id => targetTrailerIds.has(id));
    return goingToTargets.length > 0 && goingToTargets.length < sourceTrailerIds.size;
}

function getTrailerIdsAtEvent(eventId: string, packingListItemsByEvent: Map<string, PackingListItemMinimal[]>): Set<string> {
    const items = packingListItemsByEvent.get(eventId) || [];
    const ids = new Set<string>();
    for (const item of items) {
        const it = item.itemType;
        if (!it?.id) continue;
        if ((it.category?.name || '') === 'Trailers') ids.add(it.id);
    }
    return ids;
}

/** Location key: eventId for events, or storage:${storageLocationId} for storage locations */
export function locationKey(type: 'event' | 'storage', id: string): string {
    return type === 'storage' ? `storage:${id}` : id;
}

export function parseLocationKey(key: string): { type: 'event' | 'storage'; id: string } {
    if (key.startsWith('storage:')) {
        return { type: 'storage', id: key.slice(8) };
    }
    return { type: 'event', id: key };
}

/**
 * Item location data for availability checking
 */
export interface ItemLocationData {
    itemTypeId: string;
    eventId: string | null;
    storageLocationId: string | null;
    quantity: number;
}

/**
 * Generate tasks to remove excess items from sources
 *
 * For each item at each source event:
 * - If NOT needed at any target: create task to move to primary target storage (e.g. HQ)
 * - If quantity at source > quantity needed: create task to move excess to primary target storage
 *
 * @param sources - Array of source events
 * @param requirements - Map of itemTypeId to requirement data
 * @param itemLocationsByLocationKey - Map of location key (eventId or storage:id) to item locations
 * @param primaryTargetStorageLocationId - Storage location ID for "return to" (e.g. HQ), or null to skip remove-to-storage tasks
 */
export function generateRemoveTasks(
    sources: Event[],
    requirements: Map<string, Requirement>,
    itemLocationsByLocationKey: Map<string, ItemLocationData[]>,
    primaryTargetStorageLocationId: string | null
): PackingTask[] {
    const tasks: PackingTask[] = [];
    if (!primaryTargetStorageLocationId) return tasks;

    for (const source of sources) {
        const sourceLocations = itemLocationsByLocationKey.get(source.id) || [];

        for (const location of sourceLocations) {
            const itemTypeId = location.itemTypeId;
            const availableQty = location.quantity;

            const requirement = requirements.get(itemTypeId);
            const neededQty = requirement ? requirement.quantity : 0;
            const excessQty = availableQty - neededQty;

            if (excessQty > 0) {
                tasks.push({
                    itemTypeId,
                    quantity: excessQty,
                    fromEventId: source.id,
                    fromStorageLocationId: null,
                    toEventId: null,
                    toStorageLocationId: primaryTargetStorageLocationId,
                    ambiguous: false,
                    warnings: [],
                });
            } else if (neededQty === 0 && availableQty > 0) {
                tasks.push({
                    itemTypeId,
                    quantity: availableQty,
                    fromEventId: source.id,
                    fromStorageLocationId: null,
                    toEventId: null,
                    toStorageLocationId: primaryTargetStorageLocationId,
                    ambiguous: false,
                    warnings: [],
                });
            }
        }
    }

    return tasks;
}

/**
 * Allocate quantity from sources with priority (inter-event moves first, then storage locations)
 *
 * @param itemLocationsByLocationKey - Map of location key (eventId or storage:id) to item locations
 */
function allocateQuantity(
    itemTypeId: string,
    requiredQty: number,
    sources: Event[],
    sourceStorageLocationIds: string[],
    itemLocationsByLocationKey: Map<string, ItemLocationData[]>,
    targetEventId: string | null,
    targetStorageLocationId: string | null
): PackingTask[] {
    const tasks: PackingTask[] = [];
    let remainingQty = requiredQty;

    for (const source of sources) {
        if (remainingQty <= 0) break;
        const sourceLocations = itemLocationsByLocationKey.get(source.id) || [];
        const location = sourceLocations.find(loc => loc.itemTypeId === itemTypeId);
        const availableQty = location ? location.quantity : 0;
        if (availableQty > 0) {
            const qtyToTake = Math.min(remainingQty, availableQty);
            tasks.push({
                itemTypeId,
                quantity: qtyToTake,
                fromEventId: source.id,
                fromStorageLocationId: null,
                toEventId: targetEventId,
                toStorageLocationId: targetStorageLocationId,
                ambiguous: false,
                warnings: [],
            });
            remainingQty -= qtyToTake;
        }
    }

    for (const storageId of sourceStorageLocationIds) {
        if (remainingQty <= 0) break;
        const key = locationKey('storage', storageId);
        const locs = itemLocationsByLocationKey.get(key) || [];
        const location = locs.find(loc => loc.itemTypeId === itemTypeId);
        const availableQty = location ? location.quantity : 0;
        if (availableQty > 0) {
            const qtyToTake = Math.min(remainingQty, availableQty);
            tasks.push({
                itemTypeId,
                quantity: qtyToTake,
                fromEventId: null,
                fromStorageLocationId: storageId,
                toEventId: targetEventId,
                toStorageLocationId: targetStorageLocationId,
                ambiguous: false,
                warnings: [],
            });
            remainingQty -= qtyToTake;
        }
    }

    return tasks;
}

/**
 * Generate tasks to add required items to targets with smart prioritization
 *
 * For each required item at each target event:
 * - Priority 1: Take from source events (inter-event moves)
 * - Priority 2: Take from source storage locations
 */
export function generateAddTasks(
    requirements: Map<string, Requirement>,
    sources: Event[],
    sourceStorageLocationIds: string[],
    targets: Event[],
    itemLocationsByLocationKey: Map<string, ItemLocationData[]>,
    packingListItemsByEvent: Map<string, PackingListItemMinimal[]>
): PackingTask[] {
    const tasks: PackingTask[] = [];

    for (const [itemTypeId, requirement] of Array.from(requirements.entries())) {
        for (const target of requirement.events) {
            const targetPackingListItemMinimals = packingListItemsByEvent.get(target.id) || [];
            const targetItem = targetPackingListItemMinimals.find(item => item.itemType?.id === itemTypeId);
            const qtyNeeded = targetItem ? targetItem.quantity || 0 : 0;
            if (qtyNeeded <= 0) continue;
            const addTasks = allocateQuantity(
                itemTypeId,
                qtyNeeded,
                sources,
                sourceStorageLocationIds,
                itemLocationsByLocationKey,
                target.id,
                null
            );
            tasks.push(...addTasks);
        }
    }

    return tasks;
}

/**
 * Get the location key for a task's source (event or storage)
 */
function taskFromKey(task: PackingTask): string {
    if (task.fromEventId != null) return task.fromEventId;
    if (task.fromStorageLocationId != null) return locationKey('storage', task.fromStorageLocationId);
    throw new Error('PackingTask: missing source (fromEventId and fromStorageLocationId)');
}

/**
 * Check inventory availability for each task and mark tasks with warnings if insufficient quantity exists at source
 */
export function checkInventoryAndWarn(
    tasks: PackingTask[],
    itemLocationsByLocationKey: Map<string, ItemLocationData[]>,
    getLocationName: (key: string) => string
): PackingTask[] {
    return tasks.map(task => {
        const fromKey = taskFromKey(task);
        const availableQty = getAvailableAtSource(task.itemTypeId, fromKey, itemLocationsByLocationKey);
        const locationName = getLocationName(fromKey);
        const warnings = computeTaskWarnings(availableQty, task.quantity, locationName) as PackingTask['warnings'];
        return { ...task, warnings };
    });
}

/**
 * Main function to generate packing tasks for an operation
 *
 * @param operation - Operation data with source/target event and storage location IDs
 * @param itemLocationsByLocationKey - Map of location key (eventId or storage:id) to item locations
 * @param getLocationName - Location key → display name for warnings
 */
export function generatePackingTasks(
    operation: PackingOperationInput,
    sources: Event[],
    targets: Event[],
    packingListItemsByEvent: Map<string, PackingListItemMinimal[]>,
    itemLocationsByLocationKey: Map<string, ItemLocationData[]>,
    getLocationName: (locationKey: string) => string
): PackingTask[] {
    const requirements = collectRequirements(targets, packingListItemsByEvent);

    const primaryTargetStorageId =
        operation.targetStorageLocationIds && operation.targetStorageLocationIds.length > 0 ? operation.targetStorageLocationIds[0] : null;
    const removeTasks = generateRemoveTasks(sources, requirements, itemLocationsByLocationKey, primaryTargetStorageId);

    const addTasks = generateAddTasks(
        requirements,
        sources,
        operation.sourceStorageLocationIds ?? [],
        targets,
        itemLocationsByLocationKey,
        packingListItemsByEvent
    );

    const allTasks = [...removeTasks, ...addTasks];
    const tasksWithWarnings = checkInventoryAndWarn(allTasks, itemLocationsByLocationKey, getLocationName);

    const finalTasks = tasksWithWarnings.map(task => {
        const sourceEvent = task.fromEventId != null ? sources.find(s => s.id === task.fromEventId) : undefined;
        if (sourceEvent) {
            const isAmbiguous = determineAmbiguity(sourceEvent, targets, packingListItemsByEvent);
            return { ...task, ambiguous: isAmbiguous || task.ambiguous };
        }
        return task;
    });

    return finalTasks;
}

// --- Route-based task generation (Phase 1: quantity per trailer from route) ---

/**
 * Get source location keys (event IDs or storage:id) that have this item in inventory.
 */
export function getAvailableSources(
    itemTypeId: string,
    sourceEventIds: string[],
    sourceStorageLocationIds: string[],
    itemLocationsByLocationKey: Map<string, ItemLocationData[]>
): string[] {
    const out: string[] = [];
    for (const eventId of sourceEventIds) {
        const locs = itemLocationsByLocationKey.get(eventId) ?? [];
        if (locs.some(loc => loc.itemTypeId === itemTypeId && loc.quantity > 0)) {
            out.push(eventId);
        }
    }
    for (const storageId of sourceStorageLocationIds) {
        const key = locationKey('storage', storageId);
        const locs = itemLocationsByLocationKey.get(key) ?? [];
        if (locs.some(loc => loc.itemTypeId === itemTypeId && loc.quantity > 0)) {
            out.push(key);
        }
    }
    return out;
}

/**
 * Choose best source for a quantity: prefer one with enough stock.
 */
export function selectBestSource(
    itemTypeId: string,
    quantityNeeded: number,
    availableSourceKeys: string[],
    itemLocationsByLocationKey: Map<string, ItemLocationData[]>
): string | null {
    let best: string | null = null;
    let bestAvailable = 0;
    for (const key of availableSourceKeys) {
        const locs = itemLocationsByLocationKey.get(key) ?? [];
        const loc = locs.find(l => l.itemTypeId === itemTypeId);
        const available = loc?.quantity ?? 0;
        if (available >= quantityNeeded) return key;
        if (available > bestAvailable) {
            bestAvailable = available;
            best = key;
        }
    }
    return best;
}

/**
 * Build availability warnings for a task (insufficient or unavailable at source).
 */
export function checkAvailabilityWarnings(
    itemTypeId: string,
    quantityNeeded: number,
    sourceLocationKey: string | null,
    itemLocationsByLocationKey: Map<string, ItemLocationData[]>,
    getLocationName: (locationKey: string) => string
): PackingTask['warnings'] {
    if (sourceLocationKey === null) return [];
    const available = getAvailableAtSource(itemTypeId, sourceLocationKey, itemLocationsByLocationKey);
    const locationName = getLocationName(sourceLocationKey);
    return computeTaskWarnings(available, quantityNeeded, locationName) as PackingTask['warnings'];
}

/**
 * Generate packing tasks from route structure and quantity plans.
 *
 * Creates simple from → to tasks per trailer path (multi-stop).
 * Destination is always an event (from path segments); source can be event or storage.
 */
export function generatePackingTasksFromRoute(
    sourceEventIds: string[],
    sourceStorageLocationIds: string[],
    _targetEventIds: string[],
    routeStructure: RouteStructure,
    quantityPlans: Map<string, ItemQuantityPlan>,
    itemLocationsByLocationKey: Map<string, ItemLocationData[]>,
    getLocationName: (locationKey: string) => string
): PackingTask[] {
    const tasks: PackingTask[] = [];

    for (const [itemTypeId, plan] of Array.from(quantityPlans.entries())) {
        const availableSourceKeys = getAvailableSources(itemTypeId, sourceEventIds, sourceStorageLocationIds, itemLocationsByLocationKey);

        for (const [trailerId, trailerPlan] of Array.from(plan.perTrailer.entries())) {
            const quantity = trailerPlan.quantity;
            const path = routeStructure.trailerPaths.get(trailerId);
            const firstEventSeg = path?.segments.find((s): s is PathSegment & { kind: 'event' } => s.kind === 'event');
            const destinationEventId = firstEventSeg?.eventId ?? null;

            const sourceKey = selectBestSource(itemTypeId, quantity, availableSourceKeys, itemLocationsByLocationKey);
            const warnings = checkAvailabilityWarnings(itemTypeId, quantity, sourceKey, itemLocationsByLocationKey, getLocationName);

            const parsed = sourceKey != null ? parseLocationKey(sourceKey) : null;
            const fromEventId = parsed?.type === 'event' ? parsed.id : null;
            const fromStorageLocationId = parsed?.type === 'storage' ? parsed.id : null;

            tasks.push({
                itemTypeId,
                quantity,
                fromEventId,
                fromStorageLocationId,
                toEventId: destinationEventId,
                toStorageLocationId: null,
                ambiguous: false,
                warnings,
            });
        }
    }

    return tasks;
}

// --- Trip LP solution task generation (Phase 3: quantities from LP flow slice) ---

/** DAG node row for mapping index -> location */
export interface LpDagNodeRow {
    index: number;
    kind: 'source' | 'event' | 'dest';
    eventId?: string | null;
    storageLocationId?: string | null;
}

/** Edge flow row from LP solution */
export interface LpEdgeFlowRow {
    fromNodeIndex: number;
    toNodeIndex: number;
    value: number;
}

/**
 * Generate packing tasks from a trip's LP solution for one "step" (source/target sets).
 * Only edges from source node set to target node set are used; quantity = flow value.
 *
 * When packingListItemsByEvent is provided, tasks whose source is an event are checked
 * for ambiguity (same rule as standalone: multiple trailers at source, only some going to targets).
 *
 * When itemLocationsByLocationKey is also provided, task quantities are adjusted to reflect
 * current inventory progress, so a new operation created after a partially-completed one
 * only generates the remaining work:
 *
 * - Event destinations: effective_qty = min(lp_qty, max(0, packing_list_req - current_at_dest)).
 *   A running `remaining` counter per (itemType, destEvent) is shared across all edges flowing
 *   into the same destination within this call, preventing combined over-delivery when multiple
 *   sources feed the same event in a single operation.
 *
 * - Storage destinations (return/unpack): effective_qty = min(lp_qty, current_at_source).
 *   A running `available` counter per (itemType, sourceKey) prevents double-spending when
 *   multiple return edges share the same source.
 *
 * Tasks with effective_qty <= 0 are omitted. When inventory data is provided, inventory
 * shortage warnings are also attached (consistent with the standalone generation path).
 */
export function generatePackingTasksFromLpSolution(
    sourceEventIds: string[],
    targetEventIds: string[],
    sourceStorageLocationIds: string[],
    targetStorageLocationIds: string[],
    dagNodes: LpDagNodeRow[],
    edgeFlowsByItemType: Record<string, LpEdgeFlowRow[]>,
    packingListItemsByEvent?: Map<string, PackingListItemMinimal[]>,
    itemLocationsByLocationKey?: Map<string, ItemLocationData[]>
): PackingTask[] {
    const tasks: PackingTask[] = [];
    if (dagNodes.length === 0) return tasks;

    const nodeToFrom = (i: number): { eventId: string | null; storageLocationId: string | null } => {
        const n = dagNodes.find(nd => nd.index === i);
        if (!n) return { eventId: null, storageLocationId: null };
        if (n.kind === 'event' && n.eventId) return { eventId: n.eventId, storageLocationId: null };
        if (n.storageLocationId) return { eventId: null, storageLocationId: n.storageLocationId };
        return { eventId: null, storageLocationId: null };
    };

    const sourceNodeIndices = new Set<number>();
    const targetNodeIndices = new Set<number>();
    for (const n of dagNodes) {
        if (n.kind === 'source' && n.storageLocationId && sourceStorageLocationIds.includes(n.storageLocationId)) {
            sourceNodeIndices.add(n.index);
        }
        if (n.kind === 'dest' && n.storageLocationId && targetStorageLocationIds.includes(n.storageLocationId)) {
            targetNodeIndices.add(n.index);
        }
        if (n.kind === 'event' && n.eventId) {
            if (sourceEventIds.includes(n.eventId)) sourceNodeIndices.add(n.index);
            if (targetEventIds.includes(n.eventId)) targetNodeIndices.add(n.index);
        }
    }

    // Running counters for inventory-aware adjustment (only used when itemLocationsByLocationKey provided).
    // remainingNeedByDestAndItem: Map<destEventId, Map<itemTypeId, remaining_qty>>
    // availableAtSourceByItemAndSrc: Map<itemTypeId, Map<sourceLocationKey, available_qty>>
    const remainingNeedByDestAndItem = new Map<string, Map<string, number>>();
    const availableAtSourceByItemAndSrc = new Map<string, Map<string, number>>();

    const emptyLocations = new Map<string, ItemLocationData[]>();
    const itemLocMapForAdjust = (): Map<string, ItemLocationData[]> => itemLocationsByLocationKey ?? emptyLocations;

    const getOrInitRemainingNeed = (itemTypeId: string, destEventId: string): number => {
        let destMap = remainingNeedByDestAndItem.get(destEventId);
        if (!destMap) {
            destMap = new Map();
            remainingNeedByDestAndItem.set(destEventId, destMap);
        }
        if (!destMap.has(itemTypeId)) {
            const packingItems = packingListItemsByEvent?.get(destEventId) ?? [];
            const packingItem = packingItems.find(p => p.itemType?.id === itemTypeId);
            const packingQty = packingItem?.quantity ?? 0;
            const locationItems = itemLocMapForAdjust().get(destEventId) ?? [];
            const locationItem = locationItems.find(l => l.itemTypeId === itemTypeId);
            const currentQty = locationItem?.quantity ?? 0;
            destMap.set(itemTypeId, Math.max(0, packingQty - currentQty));
        }
        const need = destMap.get(itemTypeId);
        if (need === undefined) throw new Error('getOrInitRemainingNeed: invariant');
        return need;
    };

    const consumeRemainingNeed = (itemTypeId: string, destEventId: string, qty: number) => {
        const destMap = remainingNeedByDestAndItem.get(destEventId);
        if (!destMap) throw new Error('consumeRemainingNeed: missing destination map');
        const cur = destMap.get(itemTypeId);
        if (cur === undefined) throw new Error('consumeRemainingNeed: missing item quantity');
        destMap.set(itemTypeId, cur - qty);
    };

    const getOrInitAvailableAtSource = (itemTypeId: string, sourceKey: string): number => {
        let srcMap = availableAtSourceByItemAndSrc.get(itemTypeId);
        if (!srcMap) {
            srcMap = new Map();
            availableAtSourceByItemAndSrc.set(itemTypeId, srcMap);
        }
        if (!srcMap.has(sourceKey)) {
            const items = itemLocMapForAdjust().get(sourceKey) ?? [];
            const item = items.find(l => l.itemTypeId === itemTypeId);
            srcMap.set(sourceKey, item?.quantity ?? 0);
        }
        const available = srcMap.get(sourceKey);
        if (available === undefined) throw new Error('getOrInitAvailableAtSource: invariant');
        return available;
    };

    const consumeAvailableAtSource = (itemTypeId: string, sourceKey: string, qty: number) => {
        const srcMap = availableAtSourceByItemAndSrc.get(itemTypeId);
        if (!srcMap) throw new Error('consumeAvailableAtSource: missing item map');
        const cur = srcMap.get(sourceKey);
        if (cur === undefined) throw new Error('consumeAvailableAtSource: missing source quantity');
        srcMap.set(sourceKey, cur - qty);
    };

    for (const [itemTypeId, flows] of Object.entries(edgeFlowsByItemType)) {
        for (const f of flows) {
            if (f.value <= 0) continue;
            if (!sourceNodeIndices.has(f.fromNodeIndex) || !targetNodeIndices.has(f.toNodeIndex)) continue;
            const from = nodeToFrom(f.fromNodeIndex);
            const to = nodeToFrom(f.toNodeIndex);

            let effectiveQty = f.value;

            if (itemLocationsByLocationKey != null) {
                if (to.eventId != null) {
                    // Event destination: cap by packing-list remaining need (shared across all edges to this dest).
                    const remaining = getOrInitRemainingNeed(itemTypeId, to.eventId);
                    effectiveQty = Math.min(f.value, remaining);
                    if (effectiveQty > 0) consumeRemainingNeed(itemTypeId, to.eventId, effectiveQty);
                } else if (to.storageLocationId != null) {
                    // Storage destination: cap by current source inventory (shared if source used for multiple returns).
                    const sourceKey =
                        from.eventId != null
                            ? from.eventId
                            : from.storageLocationId != null
                              ? locationKey('storage', from.storageLocationId)
                              : null;
                    if (sourceKey != null) {
                        const available = getOrInitAvailableAtSource(itemTypeId, sourceKey);
                        effectiveQty = Math.min(f.value, available);
                        if (effectiveQty > 0) consumeAvailableAtSource(itemTypeId, sourceKey, effectiveQty);
                    }
                }
            }

            if (effectiveQty <= 0) continue;

            tasks.push({
                itemTypeId,
                quantity: effectiveQty,
                fromEventId: from.eventId,
                fromStorageLocationId: from.storageLocationId,
                toEventId: to.eventId,
                toStorageLocationId: to.storageLocationId,
                ambiguous: false,
                warnings: [],
            });
        }
    }

    if (packingListItemsByEvent != null && packingListItemsByEvent.size > 0) {
        for (let i = 0; i < tasks.length; i++) {
            const task = tasks[i];
            if (task.fromEventId != null) {
                const isAmbiguous = determineAmbiguityByEventIds(task.fromEventId, targetEventIds, packingListItemsByEvent);
                tasks[i] = { ...task, ambiguous: isAmbiguous };
            }
        }
    }

    // When inventory data is provided, attach shortage warnings (consistent with standalone path).
    if (itemLocationsByLocationKey != null) {
        return checkInventoryAndWarn(tasks, itemLocationsByLocationKey, key => key);
    }

    return tasks;
}
